// Combat: target acquisition, attack timers, projectiles, damage, core defenses (§17, §22).

import { buildingDef, defs, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { PlayerId, TargetPriority } from '../../content/types';
import { dist, type Vec2 } from '../../shared/vec';
import type { Sim } from '../sim';
import { physicalLineClear } from '../mobility';
import { buffMult, hasMark, type Building, type Squad } from '../state';
import {
  buildingAccessPoint,
  distanceToBuildingFootprint,
  distanceToTarget,
  isHostileTarget,
} from '../targeting';

const dt = TUNING.dt;

interface Candidate {
  id: number;
  kind: 'squad' | 'worker' | 'building' | 'mine';
  pos: Vec2;
  priorityClass: TargetPriority;
  isWorker: boolean;
  marked: boolean;
}

function classify(sim: Sim, id: number): Candidate | null {
  const sq = sim.state.squads.get(id);
  if (sq) {
    const d = unitDef(sq.defId);
    const cls: TargetPriority =
      d.sizeTag === 'giant' ? 'giant' : d.roleTags.includes('siege') ? 'siege' : d.sizeTag === 'large' ? 'large' : 'squad';
    return {
      id,
      kind: 'squad',
      pos: sq.pos,
      priorityClass: cls,
      isWorker: false,
      marked: hasMark(sq.marks, 'giant-mark'),
    };
  }
  const w = sim.state.workers.get(id);
  if (w) return { id, kind: 'worker', pos: w.pos, priorityClass: 'worker', isWorker: true, marked: false };
  const b = sim.state.buildings.get(id);
  if (b) return { id, kind: 'building', pos: b.pos, priorityClass: 'building', isWorker: false, marked: false };
  const m = sim.state.mines.get(id);
  if (m) return { id, kind: 'mine', pos: m.pos, priorityClass: 'trap', isWorker: false, marked: false };
  return null;
}

function collectEnemies(
  sim: Sim,
  owner: PlayerId,
  pos: Vec2,
  range: number,
  opts?: { meleeAttacker?: boolean },
): Candidate[] {
  const out: Candidate[] = [];
  for (const s of sim.state.squads.values()) {
    if (!sim.areHostile(owner, s.owner)) continue;
    if (s.mountedOnWall !== null) continue; // garrisoned squads are protected by the wall
    // squads riding a living host are untargetable by melee (§27)
    if (s.mountedOnSquad !== null && opts?.meleeAttacker) continue;
    if (dist(s.pos, pos) <= range) {
      const c = classify(sim, s.id);
      if (c) out.push(c);
    }
  }
  for (const w of sim.state.workers.values()) {
    if (!sim.areHostile(owner, w.owner) || w.state === 'sheltered') continue;
    if (dist(w.pos, pos) <= range) {
      const c = classify(sim, w.id);
      if (c) out.push(c);
    }
  }
  for (const b of sim.state.buildings.values()) {
    if (!sim.areHostile(owner, b.owner)) continue;
    if (distanceToBuildingFootprint(pos, b) <= range) {
      const c = classify(sim, b.id);
      if (c) out.push(c);
    }
  }
  for (const m of sim.state.mines.values()) {
    if (!sim.areHostile(owner, m.owner)) continue;
    if (dist(m.pos, pos) <= range) {
      const c = classify(sim, m.id);
      if (c) out.push(c);
    }
  }
  return out;
}

function priorityRank(list: TargetPriority[], cls: TargetPriority): number {
  const i = list.indexOf(cls);
  if (i >= 0) return list.length - i; // earlier = higher
  return 0.5; // not listed: low but attackable
}

/** Effective attack range (wall garrisons and squad mounts shoot 25% further). */
export function effAttackRange(s: Squad): number {
  const base = unitDef(s.defId).attack?.range ?? 1.5;
  return s.mountedOnWall !== null || s.mountedOnSquad !== null ? base * TUNING.mountRangeBonus : base;
}

/**
 * Pick a target for squad s per §17.3. The scan area is command-aware: a defending
 * squad watches its whole guard zone, not just its own weapon range — this is what
 * makes Defend actually engage intruders anywhere in the zone.
 */
function acquire(sim: Sim, s: Squad): void {
  const d = unitDef(s.defId);
  if (!d.attack) return;
  // Rootwarden thorn volley only exists while rooted (Core Annex System)
  if (d.attackRequiresRoot && !s.treeRooted) return;

  let center = s.pos;
  let radius = effAttackRange(s) + TUNING.acquisitionExtraRange;
  if (s.mountedOnWall !== null) {
    radius = effAttackRange(s) + 2;
  } else {
    switch (s.command.kind) {
      case 'defend':
        center = s.command.pointA ?? s.pos;
        radius = (s.command.siteId ? TUNING.defendRadius : 10) + 8;
        break;
      case 'capture': {
        const site = sim.state.sites.find((x) => x.id === s.command.siteId);
        if (site) center = site.pos;
        radius = TUNING.captureRadius + 3;
        break;
      }
      case 'escort': {
        const boss = s.escortOf !== null ? sim.state.squads.get(s.escortOf) : undefined;
        if (boss) center = boss.pos;
        radius = 14;
        break;
      }
      case 'patrol':
        radius = 12;
        break;
      case 'idle':
        center = s.command.pointA ?? s.pos;
        radius = effAttackRange(s) + 8;
        break;
      case 'raid':
        radius = 20;
        break;
      default:
        break;
    }
  }
  const isRangedUnit = d.attack.range > 4;
  const candidates = collectEnemies(sim, s.owner, center, radius, { meleeAttacker: !isRangedUnit });
  if (candidates.length === 0) return;
  const raidMode = s.command.kind === 'raid';
  // Futile-chase switching (SC2-style automatic targeting): a target stays sticky only
  // while the fight is productive. After chaseFutileSeconds without landing a hit on it —
  // a body-blocked giant, a melee squad kited by a faster runner — stickiness drops and
  // distance dominates, so the squad focuses what it can actually reach (usually the
  // blocker in its face) instead of grinding after a dying runner forever.
  const now = sim.state.time;
  const futileChase =
    s.targetId !== null &&
    now - s.targetSince > TUNING.chaseFutileSeconds &&
    now - s.lastCombatAt > TUNING.chaseFutileSeconds;
  const distanceWeight = futileChase ? 2 : 0.5;
  let best: Candidate | null = null;
  let bestScore = -Infinity;
  for (const c of candidates) {
    const targetBuilding = c.kind === 'building' ? sim.state.buildings.get(c.id) : undefined;
    const reachablePoint = targetBuilding
      ? buildingAccessPoint(s.pos, targetBuilding)
      : c.pos;
    if (!sim.nav.canReach(s.pos, reachablePoint, s.owner)) continue;
    // Route planning intentionally treats an owned closed gate as eventually passable.
    // Ranged target acquisition must use current physical space instead, or defenders can
    // shoot out through their sealed gate while attackers correctly see it as a wall.
    if (isRangedUnit && s.mountedOnWall === null && !physicalLineClear(sim, s.pos, reachablePoint, s.owner)) {
      continue;
    }
    let score = priorityRank(d.targetPriority, c.priorityClass) * 10;
    if (raidMode) {
      // Raid priority: workers > economy > production > rest (§23.6 GDD)
      if (c.isWorker) score += 100;
      else if (c.kind === 'building') score += 40;
    }
    if (c.marked && isRangedUnit) score += 60; // Giant Mark (§20.9)
    if (s.targetId === c.id && !futileChase) score *= TUNING.focusBonus;
    score -= dist(c.pos, s.pos) * distanceWeight;
    // Explicit id tiebreak rather than first-wins. Ties currently resolve by collectEnemies'
    // iteration order, which is deterministic only because it walks the entity Maps in insertion
    // order; a spatial index would change that order and silently re-resolve every tie. Scoring
    // on a total order makes acquisition independent of how candidates are gathered.
    if (score > bestScore || (score === bestScore && best !== null && c.id < best.id)) {
      bestScore = score;
      best = c;
    }
  }
  if (best) {
    if (
      s.command.kind === 'attack' &&
      s.command.targetEntity === undefined &&
      best.id !== s.targetId
    ) {
      // Attack-move pursuit is leashed to where this contact began. Measuring from the
      // moving squad instead lets a retreating enemy kite the order across the whole map.
      s.command.pointA = { ...s.pos };
    }
    if (best.id !== s.targetId) s.targetSince = now;
    s.targetId = best.id;
  }
}

function applyAttack(sim: Sim, attacker: Squad, targetId: number): void {
  const d = unitDef(attacker.defId);
  const atk = d.attack!;
  const isRanged = !!atk.projectileSpeed;
  const dmgMult = buffMult(attacker.buffs, 'damage');
  const impactWeight =
    d.sizeTag === 'giant' ? 'giant' : d.sizeTag === 'large' || d.roleTags.includes('siege') || attacker.isBoss ? 'heavy' : 'light';
  const meleeImpact = (pos: Vec2, structure: boolean): void => {
    if (!isRanged) sim.pushEvent({ type: 'MeleeImpact', pos: { ...pos }, weight: impactWeight, structure });
  };

  const targetSquad = sim.state.squads.get(targetId);
  if (targetSquad) {
    let raw = atk.damage * dmgMult;
    const mult = atk.damageVs?.[unitDef(targetSquad.defId).sizeTag];
    if (mult) raw *= mult;
    // Toll of Iron: +20% vs squads marked by this player's gate
    if (hasMark(targetSquad.marks, `gate-mark-${attacker.owner}`)) raw *= 1.2;
    // Precision Foundry / Longshot calibration marks coordinate allied precision fire.
    if (hasMark(targetSquad.marks, `building-mark-${attacker.owner}`) && isRanged) raw *= 1.15; // TEST
    if (isRanged) {
      sim.spawnProjectile(attacker.owner, attacker.pos, targetId, 'squad', atk.projectileSpeed!, raw, 1, attacker.id);
    } else {
      sim.damageSquad(targetSquad, raw, { isRanged: false, attackerOwner: attacker.owner });
      meleeImpact(targetSquad.pos, false);
    }
    return;
  }
  const targetWorker = sim.state.workers.get(targetId);
  if (targetWorker) {
    const raw = atk.damage * dmgMult;
    if (isRanged) {
      sim.spawnProjectile(attacker.owner, attacker.pos, targetId, 'worker', atk.projectileSpeed!, raw, 1, attacker.id);
    } else {
      sim.damageWorker(targetWorker, raw);
      meleeImpact(targetWorker.pos, false);
    }
    return;
  }
  const targetBuilding = sim.state.buildings.get(targetId);
  if (targetBuilding) {
    attacker.structureAttackCount++;
    const structMult = (atk.structureMult ?? 1) * buffMult(attacker.buffs, 'structureDamage');
    const raw = atk.damage * dmgMult;
    const isSiege = (atk.structureMult ?? 1) > 1 || attacker.isBoss;
    if (isRanged) {
      sim.spawnProjectile(attacker.owner, attacker.pos, targetId, 'building', atk.projectileSpeed!, raw, structMult, attacker.id);
    } else {
      sim.damageBuilding(targetBuilding, raw, {
        structureMult: structMult,
        isSiege,
        attackerOwner: attacker.owner,
        fireTag: atk.damageTag === 'fire',
      });
      meleeImpact(targetBuilding.pos, true);
    }
    return;
  }
  const targetMine = sim.state.mines.get(targetId);
  if (targetMine) {
    const raw = atk.damage * dmgMult;
    if (isRanged) {
      sim.spawnProjectile(attacker.owner, attacker.pos, targetId, 'mine', atk.projectileSpeed!, raw, 1, attacker.id);
    } else {
      sim.damageMine(targetMine, raw);
      meleeImpact(targetMine.pos, true);
    }
  }
}

/** Attack-speed multiplier including escort scaling for bosses (§20.2). */
function attackSpeedMult(sim: Sim, s: Squad): number {
  let m = buffMult(s.buffs, 'attackSpeed');
  if (s.isBoss) {
    const extras = defs.bossExtras[s.defId];
    if (extras?.escortAttackSpeedPer) {
      let escortInfantry = 0;
      for (const e of sim.state.squads.values()) {
        if (e.escortOf === s.id && unitDef(e.defId).roleTags.includes('frontline')) escortInfantry++;
      }
      m *= 1 + Math.min(extras.escortAttackSpeedCap ?? 1, escortInfantry * extras.escortAttackSpeedPer);
    }
  }
  return m;
}

function targetInRange(sim: Sim, s: Squad, targetId: number): boolean {
  const d = distanceToTarget(sim, s.pos, targetId);
  if (d === null || d > effAttackRange(s) + 0.3) return false;
  // Battlement garrisons are the deliberate over-wall exception. Every ground attacker,
  // including giants with long melee reach, needs a real open line to the target surface.
  if (s.mountedOnWall !== null) return true;
  const building = sim.state.buildings.get(targetId);
  const aim = building ? buildingAccessPoint(s.pos, building) : classify(sim, targetId)?.pos;
  return !!aim && physicalLineClear(sim, s.pos, aim, s.owner);
}

export function stepCombat(sim: Sim): void {
  const st = sim.state;

  // ---- squads
  for (const s of [...st.squads.values()]) {
    if (s.dead) continue;
    if (s.knockdownRemaining > 0) {
      s.targetId = null;
      s.engaged = false;
      continue;
    }
    const d = unitDef(s.defId);
    if (!d.attack) continue;
    if (s.command.kind === 'withdraw') continue; // never fights
    // Rootwarden thorn volley only exists while rooted (Core Annex System)
    if (d.attackRequiresRoot && !s.treeRooted) {
      s.targetId = null;
      s.engaged = false;
      continue;
    }

    if (s.attackCooldown > 0) s.attackCooldown -= dt;
    s.retargetTimer -= dt;

    // Ground move is the RTS "go there now" order. Attacking en route belongs to the
    // explicit Attack command; reacquiring here caused units retreating from a base to
    // repeatedly stop, turn, and strike it again.
    if (s.command.kind === 'move') {
      s.targetId = null;
      s.engaged = false;
      continue;
    }

    // validate current target
    if (s.targetId !== null && (!classify(sim, s.targetId) || !isHostileTarget(sim, s.owner, s.targetId))) {
      s.targetId = null;
      s.engaged = false;
      if (s.command.kind === 'attack' && s.command.targetEntity === undefined) {
        s.command.pointA = undefined;
      }
    }

    // pinned attack target keeps priority; otherwise (re)acquire on cadence
    const pinned = s.command.kind === 'attack' && s.command.targetEntity !== undefined;
    if (!pinned && s.retargetTimer <= 0) {
      s.retargetTimer = TUNING.retargetIntervalSeconds;
      acquire(sim, s);
    }

    // ---- wind-up -> strike
    //
    // Damage used to land the instant attackCooldown hit zero, with the melee root applied
    // *after* the swing. That is the same amount of frozen time, but it reads as a stutter
    // rather than as a blow: nothing on screen anticipates the hit.
    //
    // The pause now happens *before* the strike. Total duty cycle is unchanged (the wind-up is
    // carved out of the same attack period), but the attacker visibly plants and commits, which
    // is what makes melee legible — and it gives the renderer a real anticipation window to pose
    // against. No content def changes: the wind-up is derived from the existing attack period.
    if (s.windupRemaining > 0) {
      s.windupRemaining -= dt;
      if (s.windupRemaining <= 0) {
        s.windupRemaining = 0;
        // Re-validate: the target may have died or walked out of range during the wind-up. A
        // committed swing that misses is intentional — it is the counterplay the telegraph buys.
        if (s.windupTargetId !== null && targetInRange(sim, s, s.windupTargetId)) {
          applyAttack(sim, s, s.windupTargetId);
          s.lastCombatAt = st.time;
        }
        s.windupTargetId = null;
      }
    } else if (s.targetId !== null && s.attackCooldown <= 0 && targetInRange(sim, s, s.targetId)) {
      const period = d.attack.period / attackSpeedMult(sim, s);
      s.attackCooldown = period;
      const windup = Math.min(period * TUNING.windupFraction, TUNING.windupMaxSeconds);
      s.windupRemaining = windup;
      s.windupTargetId = s.targetId;
      s.lastCombatAt = st.time;
      // Melee plants for the wind-up. This replaces the old post-swing root: a fleeing
      // equal-speed target still escapes with a parting hit, and faster chasers still re-close
      // during the pause, because the pause is the same length — it just moved earlier.
      if (!d.attack.projectileSpeed) {
        s.rootedRemaining = Math.max(s.rootedRemaining, windup);
      }
      const tp = classify(sim, s.targetId);
      if (tp) s.facing = Math.atan2(tp.pos.y - s.pos.y, tp.pos.x - s.pos.x);
    }
  }

  // ---- building attacks (Royal Ballista) + Murder Slots
  for (const b of st.buildings.values()) {
    if (b.state !== 'active') continue;
    const bd = buildingDef(b.defId);

    if (bd.attack && (!bd.attackRequiresUpgrade || b.upgradeDone)) {
      if (b.attackCooldown > 0) b.attackCooldown -= dt;
      // validate/acquire
      const valid =
        b.targetId !== null &&
        (() => {
          const c = classify(sim, b.targetId!);
          return !!c && dist(c.pos, b.pos) <= bd.attack!.range;
        })();
      if (!valid) {
        b.targetId = null;
        const candidates = collectEnemies(sim, b.owner, b.pos, bd.attack.range);
        let best: Candidate | null = null;
        let bestScore = -Infinity;
        for (const c of candidates) {
          if (c.kind === 'building') continue; // ballista shoots units
          let score = priorityRank(bd.attack.targetPriority, c.priorityClass) * 10;
          score -= dist(c.pos, b.pos) * 0.2;
          if (score > bestScore) {
            bestScore = score;
            best = c;
          }
        }
        if (best) b.targetId = best.id;
      }
      if (b.targetId !== null && b.attackCooldown <= 0) {
        const c = classify(sim, b.targetId);
        if (c && c.kind !== 'building') {
          let raw = bd.attack.damage;
          if (b.isCore) raw *= sim.annexMult(b.owner, 'coreAttackMult');
          if (c.kind === 'squad') {
            const ts = st.squads.get(c.id)!;
            const mult = bd.attack.damageVs?.[unitDef(ts.defId).sizeTag];
            if (mult) raw *= mult;
            const brace = bd.traits?.find((t) => t.kind === 'braceShot');
            if (brace && st.time - b.lastDamagedAt >= brace.safeSeconds && st.time - (b.traitCounters.braceAt ?? 0) >= brace.safeSeconds) {
              raw *= brace.damageMult;
              ts.marks.push({ markId: brace.markId, remaining: brace.markSeconds });
              b.traitCounters.braceAt = st.time;
            }
            const focus = bd.traits?.find((t) => t.kind === 'focusBeam');
            if (focus) {
              const same = b.traitCounters.focusTarget === c.id;
              const shots = same ? (b.traitCounters.focusShots ?? 0) + 1 : 1;
              b.traitCounters.focusTarget = c.id;
              b.traitCounters.focusShots = shots;
              raw *= Math.min(focus.maxMult, 1 + (shots - 1) * focus.rampPerShot);
              if (shots >= focus.overheatShots) {
                b.attackCooldown = focus.overheatSeconds;
                b.traitCounters.focusShots = 0;
              }
            }
            const seed = bd.traits?.find((t) => t.kind === 'seedVolley');
            if (seed) {
              const shots = (b.traitCounters.volleys ?? 0) + 1;
              b.traitCounters.volleys = shots;
              if (shots % seed.every === 0) {
                for (const enemy of st.squads.values()) {
                  if (sim.areHostile(b.owner, enemy.owner) && dist(enemy.pos, ts.pos) <= seed.radius) {
                    enemy.buffs.push({ stat: 'moveSpeed', mult: seed.slowMult, remaining: seed.duration, source: `seedshot-${b.id}` });
                  }
                }
              }
            }
          }
          sim.spawnProjectile(
            b.owner,
            b.pos,
            c.id,
            c.kind === 'squad' ? 'squad' : 'worker',
            bd.attack.projectileSpeed ?? 20,
            raw,
            1,
            null,
          );
          const chain = bd.traits?.find((t) => t.kind === 'contestedChain');
          const contested = b.siteId !== null && st.sites.some((site) => site.id === b.siteId && site.contested);
          if (chain && contested) {
            const second = collectEnemies(sim, b.owner, b.pos, bd.attack.range)
              .filter((x) => x.kind !== 'building' && x.id !== c.id)
              .sort((a, z) => dist(a.pos, c.pos) - dist(z.pos, c.pos) || a.id - z.id)[0];
            if (second) {
              sim.spawnProjectile(
                b.owner,
                b.pos,
                second.id,
                second.kind === 'squad' ? 'squad' : 'worker',
                bd.attack.projectileSpeed ?? 20,
                raw * chain.damageMult,
                1,
                null,
              );
            }
          }
          const split = bd.traits?.find((t) => t.kind === 'focusBeam');
          if (split && b.upgradeDone && b.modeActive) {
            const second = collectEnemies(sim, b.owner, b.pos, bd.attack.range)
              .filter((x) => x.kind !== 'building' && x.id !== c.id)
              .sort((a, z) => dist(a.pos, c.pos) - dist(z.pos, c.pos) || a.id - z.id)[0];
            if (second) {
              sim.spawnProjectile(
                b.owner,
                b.pos,
                second.id,
                second.kind === 'squad' ? 'squad' : 'worker',
                bd.attack.projectileSpeed ?? 20,
                raw * 0.55,
                1,
                null,
              );
            }
          }
          if (b.attackCooldown <= 0) b.attackCooldown = bd.attack.period;
        }
      }
    }

    // Murder Slots: small melee squads in wall contact (§22.2)
    if (b.isCore) {
      const edge = Math.max(bd.footprint.w, bd.footprint.h) / 2 + TUNING.murderSlotsRange;
      for (const s of st.squads.values()) {
        if (!sim.areHostile(b.owner, s.owner)) continue;
        if (s.mountedOnWall !== null || s.mountedOnSquad !== null) continue; // riders are out of reach
        const sd = unitDef(s.defId);
        const isSmallMelee =
          (sd.sizeTag === 'small' || sd.sizeTag === 'medium') && (sd.attack?.range ?? 0) <= 3;
        if (!isSmallMelee) continue;
        if (dist(s.pos, b.pos) <= edge) {
          sim.damageSquad(s, TUNING.murderSlotsDps * dt, { isRanged: false, attackerOwner: b.owner });
        }
      }
    }
  }

  // ---- projectiles
  for (const pr of [...st.projectiles.values()]) {
    const c = classify(sim, pr.targetId);
    const targetPos = c ? c.pos : null;
    if (!targetPos) {
      pr.dead = true;
      st.projectiles.delete(pr.id);
      continue;
    }
    const step = pr.speed * dt;
    const d = dist(pr.pos, targetPos);
    if (d <= step + 0.4) {
      // impact
      switch (pr.targetKind) {
        case 'squad': {
          const ts = st.squads.get(pr.targetId);
          if (ts) sim.damageSquad(ts, pr.damage, { isRanged: true, attackerOwner: pr.owner });
          break;
        }
        case 'worker': {
          const tw = st.workers.get(pr.targetId);
          if (tw && tw.state !== 'sheltered') sim.damageWorker(tw, pr.damage);
          break;
        }
        case 'building': {
          const tb = st.buildings.get(pr.targetId);
          if (tb)
            sim.damageBuilding(tb, pr.damage, {
              structureMult: pr.structureMult,
              isSiege: pr.structureMult > 1,
              attackerOwner: pr.owner,
            });
          break;
        }
        case 'mine': {
          const tm = st.mines.get(pr.targetId);
          if (tm) sim.damageMine(tm, pr.damage);
          break;
        }
      }
      sim.pushEvent({ type: 'ProjectileHit', pos: { ...targetPos } });
      pr.dead = true;
      st.projectiles.delete(pr.id);
    } else {
      const t = step / d;
      pr.pos = { x: pr.pos.x + (targetPos.x - pr.pos.x) * t, y: pr.pos.y + (targetPos.y - pr.pos.y) * t };
    }
  }
}

export type { Building };
